import React, { useState, useEffect, useRef } from 'react';
import ETLogo from '../../../../assets/environment-testing.png';
import { useNavigate, useLocation } from 'react-router';
import {
    Box,
    Typography,
    Button,
    Paper,
    Container,
    TextField,
    Snackbar,
    Alert,
    Link
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import RefreshIcon from '@mui/icons-material/Refresh';
import { validateOtpToRegisterClient, sendOtpToRegisterClient } from 'services';

export const VerificationClientOtp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const emailId = queryParams.get("emailId");
    const email = location.state?.email || emailId;

    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [timer, setTimer] = useState(300);
    const [canResend, setCanResend] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        if (timer > 0 && !canResend) {
            const interval = setInterval(() => {
                setTimer(prev => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else if (timer === 0) {
            setCanResend(true);
        }
    }, [timer, canResend]);

    useEffect(() => {
        if (!email) {
            navigate('/registration');
        }
    }, [email, navigate]);

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d*$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');

        if (pastedData.length <= 6) {
            const newOtp = [...otp];
            for (let i = 0; i < pastedData.length && i < 6; i++) {
                newOtp[i] = pastedData[i];
            }
            setOtp(newOtp);

            const nextIndex = Math.min(pastedData.length, 5);
            inputRefs.current[nextIndex]?.focus();
        }
    };

    const isOtpComplete = otp.every(digit => digit !== '');

    const handleConfirmOtp = async () => {
        if (!isOtpComplete) return;

        setLoading(true);

        try {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');

            const otpString = otp.join('');

            const response = await validateOtpToRegisterClient({
                emailId: email,
                oTP: otpString
            });

            if (response.status === 200) {
                const jwt = response?.data.token;
                const validUserCheck: any = jwt ? JSON.parse(atob(jwt.split('.')[1])) : null;

                if (validUserCheck.email !== "" && validUserCheck.ValidOTP === "True" && validUserCheck.OnboardRequest === "True") {
                    localStorage.setItem('token', jwt);
                    setSnackbar({
                        open: true,
                        message: 'OTP verified successfully!',
                        severity: 'success'
                    });

                    setTimeout(() => {
                        navigate('/new-client-registration', {
                            state: { email: email, verified: true, token: jwt }
                        });
                    }, 1500);
                } else {
                    setSnackbar({
                        open: true,
                        message: 'Invalid OTP. Please try again.',
                        severity: 'error'
                    });
                    setOtp(['', '', '', '', '', '']);
                    inputRefs.current[0]?.focus();
                }
            } else if (response.status === 404) {
                const errorMessage = typeof response?.data === 'string'
                    ? response.data
                    : response?.data?.title || 'Invalid OTP. Please try again.';
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error'
                });
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            } else {
                const errorMessage = typeof response?.data === 'string'
                    ? response.data
                    : response?.data?.title || 'Invalid OTP. Please try again.';
                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: 'error'
                });
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            }
        } catch (error: any) {
            let errorMessage = 'OTP verification failed. Please try again.';

            if (error?.response?.status === 400) {
                errorMessage = 'Invalid OTP. Please check and try again.';
            } else if (error?.response?.status === 410) {
                errorMessage = 'OTP has expired. Please request a new one.';
            } else if (error?.response?.status === 404) {
                errorMessage = 'Invalid OTP. Please check and try again';
            }

            // Extract error message properly
            const apiError = error?.response?.data;
            const finalErrorMessage = typeof apiError === 'string'
                ? apiError
                : apiError?.title || apiError?.message || errorMessage;

            setSnackbar({
                open: true,
                message: finalErrorMessage,
                severity: 'error'
            });

            setOtp(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        setResendLoading(true);

        try {
            const response = await sendOtpToRegisterClient(email);

            if (response.status === 200) {
                setSnackbar({
                    open: true,
                    message: 'OTP resent successfully! Please check your email.',
                    severity: 'success'
                });

                setTimer(60);
                setCanResend(false);
                setOtp(['', '', '', '', '', '']);
                inputRefs.current[0]?.focus();
            } else {
                setSnackbar({
                    open: true,
                    message: 'Failed to resend OTP. Please try again.',
                    severity: 'error'
                });
            }
        } catch (error: any) {
            setSnackbar({
                open: true,
                message: error?.response?.data || 'Failed to resend OTP. Please try again.',
                severity: 'error'
            });
        } finally {
            setResendLoading(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                background: 'linear-gradient(to right, #003883, #ee7d11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 4
            }}
        >
            <Container maxWidth="sm">
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 3, md: 5 },
                        borderRadius: 3,
                        background: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8e8e8',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '5px',
                            background: 'linear-gradient(90deg, #003883 0%, #0056b3 100%)',
                        }
                    }}
                >
                    <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box sx={{ mb: 2, textAlign: 'center' }}>
                            <Link
                                href="https://www.eurofins.com.au/environment-testing/"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ display: 'inline-block' }}
                            >
                                <Box
                                    component="img"
                                    src={ETLogo}
                                    alt="Eurofins Environment Testing Logo"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                    sx={{
                                        width: {
                                            xs: '260px', // mobile
                                            sm: '320px', // tablet
                                            md: '450px', // desktop
                                        },
                                        maxWidth: '100%',
                                        height: 'auto',
                                        mb: 3,
                                    }}
                                />
                            </Link>
                        </Box>
                        <Typography
                            variant="h4"
                            component="h1"
                            sx={{
                                fontWeight: 600,
                                mb: 2,
                                fontSize: '18px',
                                color: 'secondary.main'
                            }}
                        >
                            Verify Your Email
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ mb: 1, fontSize: '12px' }}
                        >
                            We've sent a 6-digit verification code to
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: '12px', color: 'secondary.main' }}
                        >
                            {email}
                        </Typography>
                    </Box>

                    <Box sx={{ mb: 4, textAlign: 'center' }}>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mb: 3 }}
                        >
                            Enter the verification code below:
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: { xs: 0.5, sm: 1, md: 1.5 },
                                mb: 3,
                                px: 2,
                            }}
                        >
                            {otp.map((digit, index) => (
                                <TextField
                                    key={index}
                                    inputRef={(el) => (inputRefs.current[index] = el)}
                                    value={digit}
                                    onChange={(e) => handleOtpChange(index, e.target.value)}
                                    onKeyDown={(e) => handleKeyDown(index, e)}
                                    onPaste={handlePaste}
                                    variant="outlined"
                                    InputProps={{
                                        sx: {
                                            height: {
                                                xs: 42,
                                                sm: 50,
                                                md: 56,
                                            },
                                            p: 0,
                                        },
                                    }}
                                    inputProps={{
                                        maxLength: 1,
                                        inputMode: "numeric",
                                        style: {
                                            textAlign: "center",
                                            fontSize: "22px",
                                            fontWeight: 600,
                                            padding: 0,
                                        },
                                        "data-testid": `otp-input-${index}`,
                                    }}
                                    sx={{
                                        width: {
                                            xs: 42,
                                            sm: 50,
                                            md: 56,
                                        },

                                        "& .MuiOutlinedInput-root": {
                                            height: {
                                                xs: 42,
                                                sm: 50,
                                                md: 56,
                                            },
                                        },

                                        "& .MuiOutlinedInput-input": {
                                            height: "100%",
                                            padding: "0 !important",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            textAlign: "center",
                                            fontSize: "22px",
                                            fontWeight: 600,
                                            lineHeight: "normal",
                                            boxSizing: "border-box",
                                        },
                                    }}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                        <LoadingButton
                            variant="contained"
                            size="large"
                            onClick={handleConfirmOtp}
                            loading={loading}
                            disabled={!isOtpComplete || loading}
                            data-testid="confirm-otp-button"
                            sx={{
                                fontWeight: 600,
                                textTransform: 'none',
                                fontSize: '12px',
                                width: 'auto',
                                px: 4,
                                minWidth: 0
                            }}
                        >
                            {loading ? 'Verifying...' : 'Confirm OTP'}
                        </LoadingButton>
                    </Box>

                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Didn't receive the code?
                        </Typography>

                        {canResend ? (
                            <LoadingButton
                                variant="outlined"
                                onClick={handleResendOtp}
                                loading={resendLoading}
                                startIcon={<RefreshIcon />}
                                data-testid="resend-otp-button"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                {resendLoading ? 'Resending...' : 'Resend OTP'}
                            </LoadingButton>
                        ) : (
                            <Typography variant="body2" color="secondary.main">
                                Resend OTP in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')} seconds
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Link
                            component="button"
                            variant="body2"
                            onClick={() => navigate('/account-registration')}
                            sx={{
                                color: 'secondary.main',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none'
                            }}
                        >
                            Back to Registration
                        </Link>
                    </Box>

                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={handleCloseSnackbar}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbar.severity}
                            variant="filled"
                            sx={{ width: '100%' }}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                </Paper>
            </Container>
        </Box>
    );
};